import { useReducedMotion } from 'hooks/useReducedMotion';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { cn } from 'utils/cn';
import { observeReadingBand } from 'utils/readingBand';

export interface NavEntry {
    /** The `id` of the element the entry scrolls to. */
    id: string;
    label: string;
    /** A setting section's "N on"; a configuration block has none. */
    onLabel?: string;
}

const NAV_TITLE_ID = 'configuration-on-this-page';

const entryClass = (active: boolean) =>
    cn(
        'new:rounded-md flex justify-between gap-2 rounded-[10px] px-2 no-underline hover:no-underline',
        NEW_FOCUS_RING,
        active
            ? 'bg-sidebar text-primary new:bg-sidebar-accent new:text-primary-strong font-semibold'
            : 'text-fg new:text-foreground-secondary'
    );

interface Props {
    sections: NavEntry[];
    blocks: NavEntry[];
}

/**
 * "On this page": the setting sections with how many are on, a rule, then the configuration blocks. Drawn from
 * 1280px wide, sticky beside the page; the entry marked is the one being read.
 */
export function ConfigurationNav({ sections, blocks }: Props) {
    const location = useLocation();
    const reducedMotion = useReducedMotion();
    const [activeId, setActiveId] = useState<string>();
    const ids = [...sections, ...blocks].map(({ id }) => id);
    const idsKey = ids.join(' ');

    useEffect(() => {
        const elements = idsKey.split(' ').flatMap((id) => document.getElementById(id) ?? []);
        return observeReadingBand(elements, setActiveId);
    }, [idsKey]);

    if (ids.length === 0) return null;
    const current = activeId && ids.includes(activeId) ? activeId : ids[0];

    const link = (entry: NavEntry, className: string) => (
        // Anchored through the router so the page keeps its location state; the scroll is ours.
        <RouterLink
            key={entry.id}
            to={{ hash: `#${entry.id}` }}
            state={location.state}
            replace
            aria-current={entry.id === current ? 'location' : undefined}
            onClick={() =>
                document
                    .getElementById(entry.id)
                    ?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' })
            }
            className={cn(entryClass(entry.id === current), className)}
        >
            <span>{entry.label}</span>
            {entry.onLabel ? <span className="text-grey-600 shrink-0 font-normal">{entry.onLabel}</span> : null}
        </RouterLink>
    );

    return (
        <nav
            aria-labelledby={NAV_TITLE_ID}
            className="sticky top-4 hidden w-[220px] shrink-0 flex-col gap-0.5 text-[13px] leading-[18px] min-[1280px]:flex"
        >
            <p
                id={NAV_TITLE_ID}
                className="text-grey-600 new:font-semibold new:tracking-[0.06em] new:uppercase m-0 px-2 pb-1.5 text-xs font-medium"
            >
                <FormattedMessage id="back-office-settings.on-this-page" />
            </p>
            {sections.map((entry) => link(entry, 'py-1.5'))}
            {sections.length > 0 && blocks.length > 0 ? (
                <div className="bg-divider new:bg-neutral-tint mx-2 my-2 h-px" aria-hidden />
            ) : null}
            {blocks.map((entry) => link(entry, 'py-[5px]'))}
        </nav>
    );
}
